import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { useAuth0 } from '@auth0/auth0-react';
import { useRouter } from '@/hooks';
import { Box, Table, TableBody, TableContainer, TableRow, IconButton } from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { StyledTableCell, TablePaginationContainer } from '../styles';
import {
  User,
  useUsersByUserGroupQuery,
  useRemoveUsersFromUserGroupMutation,
  AllUserGroupsForUserManagementDocument,
  UsersByUserGroupDocument,
  AllUsersForAddUserDialogDocument,
} from '@/__generated__/graphql';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { UsersTableHead } from '../shared/UsersTableHead';
import { Order, UsersTableHeadCell } from '@/types';
import { useAtom } from 'jotai';
import { userManagementSearchTerm } from '../state';
import { useDebounce } from '@/hooks/useDebounce';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { loginIcons } from '../AllUsers/data';
import { RolesCell } from '../shared/RolesCell';
import { UserAvatarCell } from '../shared/UserAvatarCell';
import { TableSkeleton } from '../shared/TableSkeleton';
import { TableDataNotFound } from '../shared/TableDataNotFound';
import { TablePagination } from '../../shared/TablePagination';
import { defaultPaginationSize } from '@/constants';

const headCells: UsersTableHeadCell[] = [
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'User Name',
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email Address',
  },
  {
    id: 'connection',
    numeric: false,
    disablePadding: false,
    label: 'Login Type',
  },
  {
    id: 'roleIds',
    numeric: false,
    disablePadding: false,
    label: 'User Role',
  },
];

type Props = {
  selectedGroupID: string;
  handleAllowGroupDelete: (value: boolean) => void;
};

export const UserTable = ({ selectedGroupID, handleAllowGroupDelete }: Props) => {
  const router = useRouter();
  const currentUser = useAuth0()?.user;

  const [deleteUser, setDeleteUser] = useState<User | undefined>(undefined);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof User | '_id'>('_id');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [searchValue, setSearchTerm] = useAtom(userManagementSearchTerm);
  const searchTerm = useDebounce(searchValue, 700);

  useEffect(() => {
    return () => {
      setSearchTerm('');
    };
  }, [setSearchTerm]);

  const { loading, data: userData } = useUsersByUserGroupQuery({
    variables: {
      input: { userGroupId: selectedGroupID, q: searchTerm },
      pageInfo: {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortDir: order === 'asc' ? 1 : -1,
      },
      usersCountInput: { userGroupId: selectedGroupID, q: searchTerm },
    },
    skip: !selectedGroupID,
  });

  const { users, usersCount } = useMemo(() => {
    return {
      users: userData?.usersByUserGroup ?? [],
      usersCount: userData?.usersCount?.count ?? 0,
    };
  }, [userData]);

  useEffect(() => {
    handleAllowGroupDelete(users.length === 0);
  }, [handleAllowGroupDelete, users]);

  const handleRequestSort = (headerId: keyof User) => {
    const isAsc = orderBy === headerId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(headerId);
  };

  const handleChangePage = (newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (newRows: number) => {
    setRowsPerPage(newRows);
    setPage(0);
  };

  const handleDeleteConfirmationClose = () => {
    setDeleteUser(undefined);
  };

  const handleDeleteConfirmationOpen = (
    event: React.MouseEvent<SVGSVGElement, MouseEvent>,
    user: User
  ) => {
    event.stopPropagation();
    setDeleteUser(user);
  };

  const [deleteUserAgainstGroupMutation] = useRemoveUsersFromUserGroupMutation();
  const handleRemoveUserFromGroup = useCallback(() => {
    if (deleteUser) {
      deleteUserAgainstGroupMutation({
        variables: {
          input: {
            userGroupId: selectedGroupID,
            userIds: [deleteUser.id],
          },
        },
        refetchQueries: [
          AllUserGroupsForUserManagementDocument,
          UsersByUserGroupDocument,
          AllUsersForAddUserDialogDocument,
        ],
        onCompleted: () => {
          handleDeleteConfirmationClose();
          showSnackBar(`${deleteUser?.name} has been deleted successfully`, 'success');
        },
        onError: (error) => {
          Sentry.captureMessage(
            `Failed to delete ${deleteUser.name ?? 'user'} from group: ${error.message}`,
            {
              user: { sub: currentUser?.sub },
              extra: {
                userGroupId: selectedGroupID,
                deletedUserId: deleteUser.id,
              },
            }
          );
          showSnackBar(
            `Failed to delete ${deleteUser.name ?? 'user'} from group: ${error.message}`,
            'error'
          );
        },
      });
      if (users.length === 1 && page > 0) setPage(page - 1);
    }
  }, [
    deleteUser,
    deleteUserAgainstGroupMutation,
    selectedGroupID,
    users,
    page,
    showSnackBar,
    currentUser?.sub,
  ]);

  const handleUserClick = useCallback(
    (id: string) => {
      router.push({
        pathname: `/userManagement/user/${id}`,
      });
    },
    [router]
  );

  return (
    <Box sx={{ width: '100%', pt: 4 }}>
      <TableContainer>
        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
          {!loading && users.length > 0 && (
            <UsersTableHead
              order={order}
              orderBy={orderBy}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
          )}
          <TableBody>
            {loading && <TableSkeleton noOfSimpleColumns={3} />}
            {!loading &&
              users.map((user) => {
                const { id, name, picture, email, connection, roleIds } = user;
                return (
                  <TableRow key={id} hover tabIndex={-1} onClick={() => handleUserClick(id)}>
                    <StyledTableCell sx={{ width: '300px' }}>
                      <UserAvatarCell name={name} picture={picture} />
                    </StyledTableCell>
                    <StyledTableCell>{email}</StyledTableCell>
                    <StyledTableCell>{connection ? loginIcons(connection) : 'N/A'}</StyledTableCell>
                    <StyledTableCell>
                      <RolesCell roleIds={roleIds} />
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: 72 }}>
                      <IconButton>
                        <DeleteOutlineRoundedIcon
                          onClick={(event) => handleDeleteConfirmationOpen(event, user)}
                        />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && selectedGroupID && users.length === 0 && (
        <TableDataNotFound message="No Users Found" />
      )}
      {!loading && usersCount > 0 && (
        <TablePaginationContainer>
          <TablePagination
            count={usersCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[...defaultPaginationSize, { label: 'All', value: usersCount }]}
          />
        </TablePaginationContainer>
      )}
      <ConfirmationDialog
        open={!!deleteUser}
        title={`Removing user from the group ${deleteUser?.name}`}
        message={`Are you sure you want to remove ${deleteUser?.name} from this group? User will no longer have access
      to platforms in this group.`}
        buttonText="Remove User from Group"
        handleRemoveUserFromGroup={handleRemoveUserFromGroup}
        onClose={handleDeleteConfirmationClose}
      />
    </Box>
  );
};
